/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, styled, Theme, useMediaQuery } from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "../../../components/Button";
import MaterialSymbol from "../../../components/MaterialSymbol";
import { Modal } from "../../../components/Modal";
import { useAppDispatch } from "../../../store/rootStore";
import { TestIds } from "../../../testIds";
import { shouldForwardProp } from "../../../util/helpers";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { AddressSelectOptions, useAddressSelect } from "../../drawdown/v1/forms/AddressSelect";
import { extractZipCode, isValidZipCode } from "../../drawdown/v1/utils/extractZipCode";
import { useStoreSelector } from "../../products/components/StockAvailability/StoreSelector";
import { StoreInfo } from "../../stores/storage/stores.types";
import CartSlice, { selectCartDeliveryMode, selectCurrentCart } from "../storage/cart.slice";
import { CartDeliveryMode, DeliveryDetails } from "../storage/cart.types";
import { createDeliveryInfo } from "../utils/createDeliveryInfo";

import PickupDeliverySwitchAlert from "./PickupStoreSwitchAlertModal";

export const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

export const SelectionContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    display: "flex",
  },
}));

export const SelectionButton = styled(Button, {
  shouldForwardProp: (prop) =>
    shouldForwardProp<{ selectMode: CartDeliveryMode; mode: CartDeliveryMode }>(
      ["selectMode", "mode"],
      prop
    ),
})<{ selectMode: CartDeliveryMode; mode: CartDeliveryMode }>(({ theme, selectMode, mode }) => ({
  width: "200px",
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    flex: "1 1",
  },
  backgroundColor: selectMode === mode ? colors.primary100 : colors.primary25,
  color: colors.primary700,
  "&:hover": {
    backgroundColor: selectMode === mode ? colors.primary100 : colors.primary50,
  },
}));

interface PickUpStoreModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export interface PickupSaveState {
  accountNumber: string;
  pickupStore: StoreInfo | undefined;
  cartDeliveryMode: CartDeliveryMode;
}

export interface DeliverySaveState {
  accountNumber: string;
  delivery: {
    deliveryDetails: DeliveryDetails;
  };
}
interface PickUpStoreModalContext extends PickUpStoreModalProps {
  setOpenWithCallback: (newOpen: boolean, callback?: () => void) => void;
  footer: React.ReactNode;
  container: React.ReactNode;
  alert: React.ReactNode;
}

const PickUpStoreModalContext = React.createContext<PickUpStoreModalContext | undefined>(undefined);

export const PickUpStoreModalProvider: React.FC<{
  children: React.ReactNode | React.ReactNode[];
}> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [callback, setCallback] = useState<(() => void) | null>(null);
  const dispatch = useAppDispatch();
  const cart = useSelector(selectCurrentCart);

  // Function to set open state and execute callback if provided
  const setOpenWithCallback = useCallback((newOpen: boolean, cb?: () => void) => {
    if (cb) {
      setCallback(() => cb);
    }
    setOpen(newOpen);
  }, []);

  // Modified setOpen function to invoke callback if it exists
  const handleSetOpen = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (callback && typeof callback === "function") {
        callback();
        setCallback(null); // Clear the callback after execution
      }
    },
    [callback]
  );

  const handleSavePickupMode = (state: PickupSaveState) => {
    dispatch(
      CartSlice.actions.setPickUpStore({
        accountNumber: state.accountNumber,
        pickupStore: state.pickupStore,
      })
    );
    dispatch(
      CartSlice.actions.setDeliveryMode({
        accountNumber: state.accountNumber,
        cartDeliveryMode: state.cartDeliveryMode,
      })
    );
    setOpen(false);
  };

  const handleSaveDeliveryMode = (state: DeliverySaveState) => {
    dispatch(CartSlice.actions.setDeliveryDetails(state));
    setOpen(false);
  };
  const cartDeliveryMode = useSelector(selectCartDeliveryMode);

  const { container, footer } = usePickUpStoreComponents(
    {
      cartDeliveryMode,
      deliveryDetails: cart?.deliveryDetails,
      pickupStore: cart?.pickupStore,
    },
    handleSavePickupMode,
    handleSaveDeliveryMode,
    () => setOpen(false)
  );

  const [alertModalOpen, setAlertModalOpen] = useState(false);

  const switchAlert = React.useMemo(() => {
    return (
      <PickupDeliverySwitchAlert
        open={alertModalOpen}
        handleClose={() => setAlertModalOpen(false)}
      />
    );
  }, [alertModalOpen]);

  const contextValue = React.useMemo(() => {
    return {
      open,
      setOpen: handleSetOpen,
      setOpenWithCallback,
      footer,
      container,
      alert: switchAlert,
    } as PickUpStoreModalContext;
  }, [open, handleSetOpen, setOpenWithCallback, footer, container, switchAlert]);

  const scrollRef = useRef<HTMLElement>(null);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const modal = () => (
    <Modal
      title="Pickup or delivery?"
      open={open}
      handleClose={() => setOpen(false)}
      footer={footer}
      testId={TestIds.feat.pickUpStore.modal}
      scrollRef={scrollRef}
      maxWidth="md"
      paperProps={{
        sx: { height: isSm ? "inherit" : "844px" },
      }}
    >
      {container}
      {switchAlert}
    </Modal>
  );

  return (
    <PickUpStoreModalContext.Provider value={contextValue}>
      {modal()}
      {children}
    </PickUpStoreModalContext.Provider>
  );
};

export const usePickUpStoreComponents = (
  {
    cartDeliveryMode,
    deliveryDetails,
    pickupStore,
    addressSettings,
  }: {
    cartDeliveryMode: CartDeliveryMode | undefined;
    deliveryDetails: DeliveryDetails | undefined;
    pickupStore: StoreInfo | undefined;
    addressSettings?: AddressSelectOptions;
  },
  onSavePickup: ((state: PickupSaveState) => void) | undefined,
  onSaveDelivery: ((state: DeliverySaveState) => void) | undefined,
  onClose: () => void
) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const [value, setValue] = useState<StoreInfo | undefined>(undefined);

  const [selectMode, setSelectMode] = React.useState<CartDeliveryMode>(
    cartDeliveryMode ?? CartDeliveryMode.PICKUP
  );

  useEffect(() => {
    setSelectMode(cartDeliveryMode ?? CartDeliveryMode.PICKUP);
  }, [cartDeliveryMode]);

  useEffect(() => {
    setValue(undefined);
  }, [account]);

  const handleCloseModal = () => {
    onClose();
    setValue(pickupStore);
  };

  const addressOptions = React.useMemo(() => {
    return {
      dairyNumber: deliveryDetails?.dairySupplyNumber,
      dateDisabled: true,
      deliveryInstructions: deliveryDetails?.deliveryInstructions,
      addressNameIsRequired: true,
      isNewAddress: deliveryDetails?.isNewAddress,
      saveAddress: deliveryDetails?.saveAddress,
      urgent: deliveryDetails?.urgent,
      urgentEnabled: true,

      ...addressSettings,
    } as AddressSelectOptions;
  }, [deliveryDetails, addressSettings]);

  const {
    AddressSelectComponent,
    addressName,
    addressShort,
    dairyNumber,
    deliveryInstructions,
    saveAddress,
    selectedAddressLine,
    selectedFullAddress,
    selectedKnownAddress,
    setAddressError,
    setZipCode,
    urgent,
    zipCode,
  } = useAddressSelect(
    account?.addresses ?? [],
    {
      city: deliveryDetails?.city ?? "",
      county: "",
      dairySupplyNum: deliveryDetails?.dairySupplyNumber ?? "",
      fullAddress: deliveryDetails?.address ?? "",
      name: deliveryDetails?.addressName ?? "",
      ruralDeliveryNum: "",
      street: deliveryDetails?.streetAddress ?? "",
      streetNumber: "",
      zipCode: deliveryDetails?.postCode ?? "",
    },
    addressOptions
  );

  const StoreSelect = useStoreSelector({
    value: value,
    setValue: setValue,
  });

  const validateAddressSelect = () => {
    if (addressShort) {
      setAddressError(undefined);
    } else {
      setAddressError("Please enter an address");
    }
    if (isValidZipCode(zipCode, addressShort)) {
      setZipCode(extractZipCode(addressShort));
    } else {
      setAddressError("Please enter an address");
    }
    if (!addressName) {
      setAddressError("Please enter an address name");
    }
    return addressName && addressShort && isValidZipCode(zipCode, addressShort);
  };

  const onSaveChanges = () => {
    if (!account) {
      return;
    }
    if (selectMode === CartDeliveryMode.PICKUP && onSavePickup) {
      onSavePickup({
        accountNumber: account.accountNumber,
        cartDeliveryMode: CartDeliveryMode.PICKUP,
        pickupStore: value,
      });
      handleCloseModal();
    }
    if (selectMode !== CartDeliveryMode.DELIVERY || !onSaveDelivery) {
      return;
    }
    const deliveryInfo = createDeliveryInfo({
      addressName,
      addressShort,
      selectedAddressLine,
      selectedFullAddress,
      selectedKnownAddress,
    });

    const deliveryDetails: DeliveryDetails = {
      ...deliveryInfo,
      deliveryInstructions,
      dairySupplyNumber: dairyNumber,
      saveAddress,
      urgent,
    };

    if (validateAddressSelect()) {
      onSaveDelivery({
        accountNumber: account.accountNumber,
        delivery: {
          deliveryDetails,
        },
      });
      handleCloseModal();
    }
  };

  const footer = React.useMemo(() => {
    let disabled = false;
    switch (selectMode) {
      case CartDeliveryMode.PICKUP:
        if (value === undefined) {
          disabled = true;
        }
        break;
      case CartDeliveryMode.DELIVERY:
        if (addressShort === undefined) {
          disabled = true;
        }
        break;
    }

    return (
      <StyledButton
        data-testid={TestIds.feat.pickUpStore.saveButton}
        disabled={disabled}
        variant="contained"
        onClick={onSaveChanges}
      >
        Save changes
      </StyledButton>
    );
  }, [account, value, onSaveChanges, selectMode, addressShort]);

  const renderDeliverySelector = () => {
    return <>{AddressSelectComponent}</>;
  };

  const selectionButtons = React.useMemo(() => {
    if (!onSavePickup || !onSaveDelivery) {
      return null;
    }
    return (
      <SelectionContainer display="inline-flex" gap="16px" mb={"32px"}>
        <SelectionButton
          variant="contained"
          aria-selected={cartDeliveryMode === CartDeliveryMode.PICKUP}
          startIcon={<MaterialSymbol sx={{ fontWeight: 400 }}>location_on</MaterialSymbol>}
          selectMode={selectMode}
          mode={CartDeliveryMode.PICKUP}
          onClick={() => setSelectMode(CartDeliveryMode.PICKUP)}
          data-testid={TestIds.components.address.forPickup}
        >
          Pickup
        </SelectionButton>

        <SelectionButton
          variant="contained"
          aria-selected={selectMode === CartDeliveryMode.DELIVERY}
          startIcon={<MaterialSymbol sx={{ fontWeight: 400 }}>local_shipping</MaterialSymbol>}
          selectMode={selectMode}
          mode={CartDeliveryMode.DELIVERY}
          onClick={() => setSelectMode(CartDeliveryMode.DELIVERY)}
          data-testid={TestIds.components.address.forDelivery}
        >
          Delivery
        </SelectionButton>
      </SelectionContainer>
    );
  }, [selectMode]);

  const selectionContainer = React.useMemo(() => {
    return (
      <>
        {selectionButtons}
        {selectMode === CartDeliveryMode.PICKUP && (
          <StoreSelect.Components.Container>
            {StoreSelect.Components.selectedStore}
            {StoreSelect.Components.searchBar}
            {StoreSelect.Components.filteredList}
          </StoreSelect.Components.Container>
        )}
        {selectMode === CartDeliveryMode.DELIVERY && renderDeliverySelector()}
      </>
    );
  }, [
    StoreSelect.Components,
    selectMode,
    selectionButtons,
    value,
    setSelectMode,
    cartDeliveryMode,
    renderDeliverySelector,
  ]);

  return React.useMemo(() => {
    return {
      buttons: selectionButtons,
      container: selectionContainer,
      footer,
      selectMode,
    };
  }, [selectionButtons, selectionContainer, footer, selectMode]);
};

export const usePickUpStoreModal = () => {
  const context = useContext(PickUpStoreModalContext);
  if (!context) {
    throw new Error("usePickUpStoreModal must be used within a PickUpStoreModalProvider");
  }
  return context;
};
